import type { EventOf, Prescribed, RepwiseEvent } from '../events.ts';
import { mass, type Mass, type Unit } from '../mass.ts';
import { floorToLoadable, type Gym } from '../plates.ts';
import { collectWorkouts } from '../programme.ts';
import { isMainLift } from '../session.ts';
import { finishSession, initPlan, prescribe, type PlanState } from './engine.ts';
import type { ExerciseCatalogue } from './validate.ts';
import type { PlanConfig, PlanInputs, RpeTable } from './types.ts';

/**
 * The bridge between the event log and a hosted plan (ADR-0020). The log stays the only source of truth: a plan's
 * progress is replayed from `plan_selected` and the finished plan workouts after it, through the interpreter.
 * Applies from the selection's log position forward, so events that arrive later from another device never
 * rewrite history before it (the review-B1 class).
 */

export type Selection = EventOf<'plan_selected'> & { index: number };

/** The plan in force at the end of the log; null = the built-in 5×5 (no selection, or a return to it). */
export function activeSelection(events: readonly RepwiseEvent[]): Selection | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.type === 'plan_selected') return e.planId ? { ...e, index: i } : null;
  }
  return null;
}

export interface PlanSession {
  workoutId: string;
  workout: string;
  at: number;
  /** Reps per set, per exercise, in the plan workout's order (null = not logged). */
  logged: (number | null)[][];
  /** Position of its workout_finished in the log (orders plan_weight_set against sessions). */
  index?: number;
}

/**
 * J27 (ADR-0023): a selection that adopts a newer version of the same plan continues the earlier selection, so the
 * versions in the chain all count and progress starts at the first selection of the chain.
 */
function chain(events: readonly RepwiseEvent[], sel: Selection): { versions: Set<number>; from: number; windows: [number, number][] } {
  const versions = new Set<number>([sel.version ?? 1]);
  const nextChoice = (i: number) => {
    for (let j = i + 1; j < events.length; j++) if (events[j]!.type === 'plan_selected') return j;
    return Infinity;
  };
  const windows: [number, number][] = [[sel.index, nextChoice(sel.index)]];
  let from = sel.index;
  let contiguous = true; // `from` follows only upgrades (the progress window the next workout continues)
  let cur: Selection = sel;
  for (let guard = 0; (cur.upgradeOf || cur.resumeOf) && guard < 50; guard++) {
    const upgrade = !!cur.upgradeOf;
    const i = events.findIndex((e) => e.id === (cur.upgradeOf ?? cur.resumeOf));
    const prev = events[i];
    if (i < 0 || i >= cur.index || prev?.type !== 'plan_selected' || prev.planId !== sel.planId) break;
    // an upgrade continues only the selection in force just before it (no other choice in between)
    if (upgrade && events.some((x, j) => j > i && j < cur.index && x.type === 'plan_selected')) break;
    windows.push([i, Math.min(nextChoice(i), cur.index)]);
    if (upgrade && contiguous) from = i;
    else contiguous = false;
    cur = { ...prev, index: i };
    versions.add(cur.version ?? 1);
  }
  return { versions, from, windows };
}

/** Finished, non-deleted workouts of this selection (and the versions it upgraded from), in log order. */
export function planSessions(events: readonly RepwiseEvent[], sel: Selection): PlanSession[] {
  const { versions, windows } = chain(events, sel);
  const workouts = collectWorkouts(events);
  const deleted = new Set(events.filter((e) => e.type === 'workout_deleted').map((e) => (e as EventOf<'workout_deleted'>).workoutId));
  const done = new Set<string>();
  const out: PlanSession[] = [];
  events.forEach((e, i) => {
    if (!windows.some(([a, b]) => i > a && i < b) || e.type !== 'workout_finished' || done.has(e.workoutId) || deleted.has(e.workoutId)) return;
    done.add(e.workoutId);
    const w = workouts.get(e.workoutId);
    const ref = w?.started.plan;
    if (!w || !ref || ref.id !== sel.planId || !versions.has(ref.version)) return;
    // a plan_selected between the chain's start and this selection (another plan, then back) breaks the chain
    const logged = w.started.prescriptions.map((p) => {
      const n = p.setPlan?.length ?? p.sets;
      const reps: (number | null)[] = Array.from({ length: n }, () => null);
      for (const s of w.sets.values()) if (s.exercise === p.exercise && s.kind === 'work' && s.index < n) reps[s.index] = s.reps;
      return reps;
    });
    if (logged.every((x) => x.every((r) => r === null))) return; // nothing done: doesn't advance (as built-in, review B3)
    // ADR-0025: an exercise lightened today counts as done but not passed, so its track repeats (holds)
    const held = w.lightened ? w.started.prescriptions.map((p, k) => (w.lightened!.exercises.has(p.exercise) && logged[k]!.some((r) => r !== null) ? logged[k]!.map((r) => (r === null ? null : 0)) : logged[k]!)) : logged;
    out.push({ workoutId: e.workoutId, workout: ref.workout, at: e.at, logged: held, index: i });
  });
  return out;
}

export function livePlanState(events: readonly RepwiseEvent[], sel: Selection, plan: PlanConfig, table: RpeTable): PlanState {
  const unit = sel.unit ?? 'kg';
  let st = initPlan(plan, unit, (sel.inputs ?? {}) as PlanInputs, table);
  // ADR-0025 (FB-002): a weight the lifter set on Train scales its track from that point in the log on
  const { windows } = chain(events, sel);
  const sets = events.map((e, i) => [e, i] as const).filter(([e, i]) => e.type === 'plan_weight_set' && windows.some(([a, b]) => i > a && i < b)) as (readonly [EventOf<'plan_weight_set'>, number])[];
  let k = 0;
  const applySets = (upTo: number) => {
    for (; k < sets.length && sets[k]![1] < upTo; k++) {
      const [e] = sets[k]!;
      const ts = st.tracks[e.track];
      if (ts && e.from > 0) st = { ...st, tracks: { ...st.tracks, [e.track]: { ...ts, value: +(ts.value * (e.to / e.from)).toFixed(6) } } };
    }
  };
  for (const s of planSessions(events, sel)) {
    applySets(s.index ?? Infinity);
    st = finishSession(plan, st, unit, s.logged, s.workout);
  }
  applySets(Infinity);
  return st;
}

/** "Do Workout C instead" for a hosted plan: a next_workout_set after the last plan session. */
export function nextOverride(events: readonly RepwiseEvent[], sel: Selection, plan: PlanConfig): string | undefined {
  const sessions = planSessions(events, sel);
  // from the chain's start (ADR-0023): a "do C instead" made under v1 still holds after adopting v2
  const last = sessions.length ? events.findIndex((e) => e.type === 'workout_finished' && e.workoutId === sessions.at(-1)!.workoutId) : chain(events, sel).from;
  for (let i = events.length - 1; i > last; i--) {
    const e = events[i]!;
    if (e.type === 'next_workout_set' && plan.rotation.includes(e.kind)) return e.kind;
  }
  return undefined;
}

/**
 * The next workout as prescriptions the session machine runs. Each set keeps the plan's number as `target`; a
 * barbell set the gym can't make exactly loads the nearest weight below it (J17 acceptance b).
 */
export function planPrescriptions(plan: PlanConfig, st: PlanState, unit: Unit, gym: Gym, catalogue?: ExerciseCatalogue, override?: string, swaps: Record<string, Swap> = {}): { workout: string; week: number; prescriptions: Prescribed[] } {
  const p = prescribe(plan, st, override);
  const prescriptions = p.exercises.map((e): Prescribed => {
    const track = plan.tracks.find((t) => t.id === e.track)!;
    const swap = swaps[track.id]?.to !== track.exercise ? swaps[track.id] : undefined; // swapped back = the plan's own
    const exercise = swap ? swap.to : track.exercise;
    const ratio = swap?.ratio ?? 1;
    const cat = catalogue?.exercises.find((x) => x.id === exercise);
    const barbell = cat ? cat.equipment.includes('barbell') : isMainLift(exercise);
    const bodyweight = swap ? (cat?.bodyweight ?? false) : (track.bodyweight ?? cat?.bodyweight ?? false);
    const def = plan.workouts.find((w) => w.id === p.workout)!.exercises.find((x) => x.track === e.track)!;
    const setPlan = e.sets.map((s) => {
      const step = unit === 'kg' ? 0.5 : 1; // a swapped, scaled non-barbell weight rounds to a sensible step
      const target: Mass = mass(ratio === 1 ? s.weight : barbell ? s.weight * ratio : Math.round((s.weight * ratio) / step) * step);
      // Never below the empty bar: a plan number under it (a light start) loads the bar (it can't load less).
      const weight: Mass | null = bodyweight ? null : barbell ? Math.max(gym.bar, floorToLoadable(gym, target) ?? gym.bar) : target;
      return { reps: s.reps, weight, kind: s.kind, ...(weight !== target ? { target } : {}) };
    });
    const heaviest = setPlan.reduce<Mass | null>((m, s) => (s.weight === null ? m : m === null || s.weight > m ? s.weight : m), null);
    return {
      exercise,
      track: track.id,
      name: cat?.name ?? (swap ? exercise : track.label.replace(/\s*\((Main|Secondary|Assistance)\)$/, '')),
      ...(swap ? { swappedFrom: track.exercise } : {}),
      weight: heaviest,
      sets: setPlan.length,
      reps: setPlan[0]?.reps ?? 5,
      reason: 'FIRST',
      setPlan,
      barbell,
      ...(bodyweight ? { bodyweight: true } : {}),
      ...(cat?.perHand && !barbell ? { perHand: true } : {}),
      ...(def.sets.some((s) => s.kind === 'ramp') ? { warmup: false } : {}),
    };
  });
  return { workout: p.workout, week: p.week, prescriptions };
}

/**
 * Programme lifecycle as an explicit state machine (ADR-0021). Every screen asks this one function which state
 * the user is in, instead of combining flags:
 *   none → builtin                       (onboarding writes programme_started)
 *   builtin ⇄ hosted                     (plan_selected with an id / with null)
 *   hosted: ready | needs-download (another device chose it; this one hasn't got the file) | block-complete
 */
export type ProgrammeMode =
  | { kind: 'none' }
  | { kind: 'builtin' }
  | { kind: 'hosted'; status: 'needs-download'; planId: string; version: number }
  | { kind: 'hosted'; status: 'ready' | 'block-complete'; planId: string; version: number; week: number; sessionsDone: number; /** BL01: the user chose "Keep going". */ continued?: boolean };

export function programmeMode(events: readonly RepwiseEvent[], loaded: (id: string, version: number) => PlanConfig | undefined): ProgrammeMode {
  if (!events.some((e) => e.type === 'programme_started')) return { kind: 'none' };
  const sel = activeSelection(events);
  if (!sel) return { kind: 'builtin' };
  const version = sel.version ?? 1;
  const plan = loaded(sel.planId!, version);
  if (!plan) return { kind: 'hosted', status: 'needs-download', planId: sel.planId!, version };
  const done = planSessions(events, sel).length;
  const week = Math.floor(done / plan.daysPerWeek) + 1;
  const continued = events.some((e, i) => i > sel.index && e.type === 'block_continued' && e.selectionId === sel.id);
  return { kind: 'hosted', status: week > plan.blockWeeks ? 'block-complete' : 'ready', planId: plan.id, version: plan.version, week, sessionsDone: done, ...(continued ? { continued } : {}) };
}

export interface CarryOver {
  /** Display units: the weight and reps of the set the suggestion comes from. */
  weight: number;
  reps: number;
  /** Working sets in that workout (5 for a 5×5). */
  sets: number;
  at: number;
  /** Where it came from: the built-in 5×5 or a hosted plan's id. */
  from: string | null;
}

/**
 * PR05 carry-over (J16): each lift's last successful set, as the suggested input for a new plan (with effort
 * "1 more", RPE 9, and the plan's default on-ramp; the user can edit both). "Successful" means every working set of
 * that exercise in the workout hit its own target reps at its planned weight or heavier; the suggestion is that
 * workout's heaviest working set. Only finished, non-deleted workouts in the programme's current unit count
 * (a workout in another unit is skipped rather than converted). Imported history is not used.
 */
export function carryOver(events: readonly RepwiseEvent[], unit: Unit, exercises: readonly string[]): Record<string, CarryOver> {
  const workouts = collectWorkouts(events);
  const finished = new Map<string, number>();
  for (const e of events) if (e.type === 'workout_finished' && !finished.has(e.workoutId)) finished.set(e.workoutId, e.at);
  for (const e of events) if (e.type === 'workout_deleted') finished.delete(e.workoutId);
  const out: Record<string, CarryOver> = {};
  const ordered = [...finished.entries()].sort((a, b) => b[1] - a[1]);
  for (const ex of exercises) {
    for (const [id, at] of ordered) {
      const w = workouts.get(id);
      if (!w || w.started.config.gym.unit !== unit) continue;
      const p = w.started.prescriptions.find((x) => x.exercise === ex);
      if (!p || p.bodyweight) continue;
      const plan = p.setPlan ?? Array.from({ length: p.sets }, () => ({ reps: p.reps, weight: p.weight }));
      const sets = [...w.sets.values()].filter((s) => s.exercise === ex && s.kind === 'work');
      const ok = plan.length > 0 && plan.every((t, i) => {
        const s = sets.find((x) => x.index === i);
        return !!s && s.reps !== null && s.reps >= t.reps && (t.weight === null || (s.weight ?? 0) >= t.weight);
      });
      if (!ok) continue;
      const top = sets.reduce((m, s) => ((s.weight ?? 0) > (m.weight ?? 0) ? s : m));
      if (top.weight === null || top.reps === null) continue;
      out[ex] = { weight: top.weight / 100, reps: top.reps, sets: plan.length, at, from: w.started.plan?.id ?? null };
      break;
    }
  }
  return out;
}

export interface UpgradeChange {
  exercise: string;
  name: string;
  set: number;
  from: string;
  to: string;
}

/**
 * PU01 (J27): what the next workout would be under a newer version of the user's plan, against today's. Nothing is
 * written; adopting writes `plan_selected` with `upgradeOf` (ADR-0023).
 */
export function previewUpgrade(events: readonly RepwiseEvent[], sel: Selection, current: PlanConfig, next: PlanConfig, table: RpeTable, gym: Gym, catalogue?: ExerciseCatalogue): { workout: { from: string; to: string }; changes: UpgradeChange[]; event: EventOf<'plan_selected'> } {
  const unit = sel.unit ?? 'kg';
  const event: EventOf<'plan_selected'> = { type: 'plan_selected', id: `upgrade-${sel.id}`, at: sel.at, planId: next.id, version: next.version, unit, upgradeOf: sel.id, ...(sel.variation ? { variation: sel.variation } : {}), ...(sel.inputs ? { inputs: sel.inputs } : {}) };
  const after = [...events, event];
  const sel2 = activeSelection(after)!;
  const a = planPrescriptions(current, livePlanState(events, sel, current, table), unit, gym, catalogue, nextOverride(events, sel, current));
  const b = planPrescriptions(next, livePlanState(after, sel2, next, table), unit, gym, catalogue, nextOverride(after, sel2, next));
  const fmt = (s: { reps: number; weight: Mass | null } | undefined) => (s ? `${s.weight === null ? '' : `${s.weight / 100} ${unit} × `}${s.reps}` : 'none');
  const changes: UpgradeChange[] = [];
  const ids = [...new Set([...a.prescriptions, ...b.prescriptions].map((p) => p.exercise))];
  for (const ex of ids) {
    const pa = a.prescriptions.find((p) => p.exercise === ex);
    const pb = b.prescriptions.find((p) => p.exercise === ex);
    const sa = pa?.setPlan ?? [];
    const sb = pb?.setPlan ?? [];
    for (let i = 0; i < Math.max(sa.length, sb.length); i++) {
      const from = fmt(sa[i]);
      const to = fmt(sb[i]);
      if (from !== to) changes.push({ exercise: ex, name: (pb ?? pa)!.name ?? ex, set: i + 1, from, to });
    }
  }
  return { workout: { from: a.workout, to: b.workout }, changes, event: { ...event, id: '' } };
}

export interface Swap {
  to: string;
  scope: 'next' | 'ongoing';
  ratio?: number;
  id: string;
}

/**
 * J18: the swaps in force for the next workout of this selection (and the versions it upgraded from). The latest
 * "from now on" per track holds until swapped back; a "just next" applies only until the next finished plan workout.
 */
export function activeSwaps(events: readonly RepwiseEvent[], sel: Selection): Record<string, Swap> {
  const { from } = chain(events, sel);
  const sessions = planSessions(events, sel);
  const lastDone = sessions.length ? events.findIndex((e) => e.type === 'workout_finished' && e.workoutId === sessions.at(-1)!.workoutId) : -1;
  const ongoing: Record<string, Swap> = {};
  const next: Record<string, Swap> = {};
  events.forEach((e, i) => {
    if (i <= from || e.type !== 'exercise_swapped') return;
    const sw: Swap = { to: e.to, scope: e.scope, id: e.id, ...(e.ratio && e.ratio !== 1 ? { ratio: e.ratio } : {}) };
    if (e.scope === 'ongoing') {
      ongoing[e.track] = sw;
      delete next[e.track];
    } else if (i > lastDone) next[e.track] = sw;
  });
  const out: Record<string, Swap> = { ...ongoing, ...next };
  return out;
}

/**
 * J16(c): the latest earlier selection of this plan that has workouts (not the one in force), so going back to the
 * plan can resume where it left off instead of starting again.
 */
export function resumable(events: readonly RepwiseEvent[], planId: string): { selection: Selection; sessions: number } | null {
  const active = activeSelection(events);
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.type !== 'plan_selected' || e.planId !== planId || (active && active.planId === planId && active.index === i)) continue;
    const sel: Selection = { ...e, index: i };
    const n = planSessions(events, sel).length;
    if (n > 0) return { selection: sel, sessions: n };
  }
  return null;
}

/**
 * Intermediate (J18): the plan with a variation applied. Pure; the default (first) variation returns the plan as
 * encoded. The variation tracks take their exercise, % of the parent's input and reps (sets and target) from it.
 */
export function withVariation(plan: PlanConfig, id: string | undefined): PlanConfig {
  const va = plan.variations?.find((v) => v.id === id);
  if (!va || va === plan.variations![0]) return plan;
  const p: PlanConfig = structuredClone(plan);
  for (const [tid, x] of Object.entries(va.tracks)) {
    const t = p.tracks.find((y) => y.id === tid);
    if (!t) continue;
    t.exercise = x.exercise;
    t.targetReps = x.reps;
    if (t.inputFrom) t.inputFrom = { ...t.inputFrom, pct: x.pct };
    for (const w of p.workouts) for (const e of w.exercises) if (e.track === tid) e.sets = e.sets.map((s) => ({ ...s, reps: x.reps }));
  }
  return p;
}

/** The plan as this selection runs it (its variation, if any). */
export const planFor = (plan: PlanConfig, sel: { variation?: string } | null | undefined) => withVariation(plan, sel?.variation);
